import { observer } from 'mobx-react-lite';
import { FC, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { useCreateBenefitSet } from '../../../../pages/actions/createBenefitSet/hooks/useCreateBenefitSet';
import { useCardOffer } from '../../../../queries/pxtraCard';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { RadioButton } from '../../atomic/input/radio';
import { Switch } from '../../atomic/input/switch';

type ViewProps = {
  cardEnabled: boolean;
  physicalCardEnabled: boolean;
  onCardEnabledChange: (enabled: boolean) => void;
  onPhysicalChange: (enabled: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  /** The stored offer is unknown, so the controls stay locked rather than
   * overwrite it. */
  loadError?: boolean;
  /** False while the card backend does not know the set yet. Saving waits for
   * it, so the controls stay usable. */
  setKnown?: boolean;
};

const CARD = 'card';
const VOUCHER = 'voucher';

/** How employees in a benefit set receive their Sachbezug: vouchers only, or
 * the pxtra Card on top, optionally as a physical card. */
export const CardOfferPanelView: FC<ViewProps> = ({
  cardEnabled,
  physicalCardEnabled,
  onCardEnabledChange,
  onPhysicalChange,
  disabled = false,
  loading = false,
  loadError = false,
  setKnown = true
}) => {
  const { t } = useTranslation();
  const locked = disabled || loading || loadError;

  return (
    <div className="flex flex-col gap-y-4 rounded-xl border border-solid border-grey-100 p-6">
      <div className="flex flex-col gap-y-1">
        <div className="font-medium">{t('benefitSet:cardOfferTitle')}</div>
        <div className="text-sm text-grey-600">{t('benefitSet:cardOfferDescription')}</div>
      </div>
      {loadError && (
        <FeedbackAlert severity="error" description={t('benefitSet:cardOfferLoadError')} />
      )}
      {!setKnown && (
        <FeedbackAlert
          severity="info"
          title={t('benefitSet:cardOfferSetNotSyncedTitle')}
          description={t('benefitSet:cardOfferSetNotSyncedDescription')}
        />
      )}
      <RadioButton
        name="cardOffer"
        displayInline={false}
        value={cardEnabled ? CARD : VOUCHER}
        options={[
          {
            value: VOUCHER,
            text: t('benefitSet:cardOfferOptionVoucher'),
            hint: t('benefitSet:cardOfferOptionVoucherHint'),
            disabled: locked
          },
          {
            value: CARD,
            text: t('benefitSet:cardOfferOptionCard'),
            hint: t('benefitSet:cardOfferOptionCardHint'),
            disabled: locked
          }
        ]}
        onChange={(event: SyntheticEvent) =>
          onCardEnabledChange((event.target as HTMLInputElement).value === CARD)
        }
      />
      <div className="pl-8">
        <Switch
          className="ml-0"
          label={t('benefitSet:cardOfferPhysicalLabel')}
          helperText={t('benefitSet:cardOfferPhysicalHint')}
          checked={physicalCardEnabled}
          disabled={locked || !cardEnabled}
          onChange={(_event, checked) => onPhysicalChange(checked)}
        />
      </div>
      <div className="text-sm text-grey-600">{t('benefitSet:cardOfferLeadTime')}</div>
    </div>
  );
};

/** The panel in the benefit selection step, bound to the benefit set store. */
export const CardOfferPanel = observer(({ disabled }: { disabled?: boolean }) => {
  const params = useParams();
  const { companyStore } = useStore();
  const { newBenefitSet } = useCreateBenefitSet();
  const isEdit = params.type === 'edit_benefit_set';
  const benefitSetId = newBenefitSet.editingBenefitSetId;
  const { data, isError } = useCardOffer(companyStore.company?.id ?? '', benefitSetId, {
    enabled: isEdit
  });
  const seeded = !isEdit || newBenefitSet.cardOfferLoadedFor === benefitSetId;

  return (
    <CardOfferPanelView
      cardEnabled={newBenefitSet.cardEnabled}
      physicalCardEnabled={newBenefitSet.physicalCardEnabled}
      onCardEnabledChange={(enabled) => (newBenefitSet.cardEnabled = enabled)}
      onPhysicalChange={(enabled) => (newBenefitSet.physicalCardEnabled = enabled)}
      disabled={disabled}
      loading={!seeded && !isError}
      loadError={isError}
      setKnown={data?.setKnown ?? true}
    />
  );
});
